
// Generic presentation mapper for accounting entries.

export function buildAccountingPresentation(entries = []) {
  const normalizedEntries = Array.isArray(entries) ? entries : [];
  const groups = new Map();

  normalizedEntries.forEach((entry, index) => {
    const identity = resolveAccountingSourceIdentity(entry, index);
    const groupMapKey = `${identity.sourceType}:${identity.groupKey}`;

    if (!groups.has(groupMapKey)) {
      groups.set(groupMapKey, {
        type: "accounting_source_group",
        key: `accounting_source:${groupMapKey}`,
        sourceType: identity.sourceType,
        sourceUuid: identity.sourceUuid,
        receiptUuid: identity.receiptUuid,
        compatibilityMode: identity.compatibilityMode,
        firstIndex: index,
        entries: [],
      });
    }

    groups.get(groupMapKey).entries.push(entry);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      summary: summarizeAccountingEntries(group.entries),
    }))
    .sort((left, right) => left.firstIndex - right.firstIndex);
}

export function resolveAccountingSourceIdentity(entry, index = 0) {
  if (entry?.source_uuid) {
    const sourceUuid = String(entry.source_uuid);
    const sourceType = normalizeSourceType(entry?.source_type);

    return {
      groupKey: sourceUuid,
      sourceType,
      sourceUuid,
      receiptUuid: entry?.receipt_uuid
        ? String(entry.receipt_uuid)
        : null,
      compatibilityMode: false,
    };
  }

  if (entry?.receipt_uuid) {
    const receiptUuid = String(entry.receipt_uuid);
    const sourceType =
      entry?.source_type === "legacy"
        ? "sales_receipt"
        : normalizeSourceType(entry?.source_type);

    return {
      groupKey: receiptUuid,
      sourceType,
      sourceUuid: receiptUuid,
      receiptUuid,
      compatibilityMode: entry?.source_type === "legacy",
    };
  }

  const fallbackUuid = String(
    entry?.uuid || `unidentified-entry:${index}`
  );

  return {
    groupKey: fallbackUuid,
    sourceType: normalizeSourceType(entry?.source_type),
    sourceUuid: null,
    receiptUuid: null,
    compatibilityMode: false,
  };
}

export function summarizeAccountingEntries(entries = []) {
  const accountStats = new Map();

  entries.forEach((entry, index) => {
    const accountCode =
      normalizeAccountCode(entry?.account_code) || "Compte non renseigné";

    if (!accountStats.has(accountCode)) {
      accountStats.set(accountCode, {
        accountCode,
        firstIndex: index,
        entries: [],
        debit: 0,
        credit: 0,
        vatAmount: 0,
      });
    }

    const stat = accountStats.get(accountCode);

    stat.entries.push(entry);
    stat.debit += toAmount(entry?.debit);
    stat.credit += toAmount(entry?.credit);
    stat.vatAmount += toAmount(entry?.vat_amount);
  });

  const accounts = Array.from(accountStats.values())
    .sort((left, right) => left.firstIndex - right.firstIndex)
    .map(({ firstIndex, ...account }) => account);

  return {
    accounts,
    debitTotal: entries.reduce(
      (total, entry) => total + toAmount(entry?.debit),
      0
    ),
    creditTotal: entries.reduce(
      (total, entry) => total + toAmount(entry?.credit),
      0
    ),
    vatTotal: entries.reduce(
      (total, entry) => total + toAmount(entry?.vat_amount),
      0
    ),
    balanced:
      Math.abs(
        entries.reduce(
          (total, entry) =>
            total + toAmount(entry?.debit) - toAmount(entry?.credit),
          0
        )
      ) < 0.005,
  };
}

// Compatibility export for callers outside AccountingPanel during migration.
export const summarizeSalesReceiptEntries = summarizeAccountingEntries;

export const ACCOUNTING_SOURCE_PRESENTATIONS = Object.freeze({
  sales_receipt: Object.freeze({
    title: "Vente",
    badge: "Ticket",
    filterLabel: "Tickets / ventes",
    filterOrder: 20,
  }),
  supplier_invoice: Object.freeze({
    title: "Facture fournisseur",
    badge: "Achat",
    filterLabel: "Factures fournisseurs",
    filterOrder: 30,
  }),
  cash_session_z: Object.freeze({
    title: "Clôture Z",
    badge: "Caisse",
    filterLabel: "Clôtures Z",
    filterOrder: 10,
  }),
  activity: Object.freeze({
    title: "Activité",
    badge: "Activité",
    filterLabel: "Activités",
    filterOrder: 40,
  }),
  legacy: Object.freeze({
    title: "Écriture historique",
    badge: "Legacy",
    filterLabel: "Écritures historiques",
    filterOrder: 900,
  }),
  unknown: Object.freeze({
    title: "Écriture comptable",
    badge: "Source",
    filterLabel: "Sources non identifiées",
    filterOrder: 950,
  }),
});

export function getAccountingSourcePresentation(sourceType) {
  const normalized = normalizeSourceType(sourceType);

  return (
    ACCOUNTING_SOURCE_PRESENTATIONS[normalized] || {
      title: formatSourceType(normalized),
      badge: "Source",
      filterLabel: formatSourceType(normalized),
      filterOrder: 1000,
    }
  );
}

export function buildAccountingSourceFilterOptions(entries = []) {
  const normalizedEntries = Array.isArray(entries) ? entries : [];
  const availableTypes = new Map();

  normalizedEntries.forEach((entry, index) => {
    const identity = resolveAccountingSourceIdentity(entry, index);
    const sourceType = identity.sourceType;

    if (availableTypes.has(sourceType)) {
      return;
    }

    const presentation = getAccountingSourcePresentation(sourceType);

    availableTypes.set(sourceType, {
      value: sourceType,
      label: presentation.filterLabel || presentation.title,
      order: Number.isFinite(presentation.filterOrder)
        ? presentation.filterOrder
        : 1000,
      firstIndex: index,
    });
  });

  return Array.from(availableTypes.values())
    .sort((left, right) => {
      if (left.order !== right.order) {
        return left.order - right.order;
      }

      if (left.order >= 1000 && right.order >= 1000) {
        return left.firstIndex - right.firstIndex;
      }

      return left.label.localeCompare(right.label, "fr");
    })
    .map(({ order, firstIndex, ...option }) => option);
}

export function matchesAccountingSourceFilter(entry, sourceFilter) {
  if (!sourceFilter || sourceFilter === "all") {
    return true;
  }

  return (
    resolveAccountingSourceIdentity(entry).sourceType === sourceFilter
  );
}

function normalizeSourceType(value) {
  const normalized = String(value || "").trim();
  return normalized || "unknown";
}

function normalizeAccountCode(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function formatSourceType(value) {
  return String(value || "unknown")
    .split("_")
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1).toLowerCase()
    )
    .join(" ");
}

function toAmount(value) {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount : 0;
}
